import { Button, Dropdown, Popover } from 'antd';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Loader from 'components/molecules/Loader';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { calculateContainerHeight, formatNumWithKMBAlt, getConfig, scaleMaxValue } from 'utils/helperMethods';
import useProfitGrowthDriversData from './hooks/useProfitGrowthDriversData';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { isEmpty, startCase } from 'lodash';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import DownSvg from 'assets/svg/Down';
import { UserContext } from 'contexts/ConfigContext';
import { useGrowthMarginDriversContext } from './context/GrowthMarginDriversContext';
import WaterFallChartIndividualised from 'components/molecules/WaterFallChartIndividualised';
import { FilterContextData } from 'contexts/FilterContext';

const MemoisedWaterFallChart = memo(
    ({
        data,
        currentGraphWidth,
        chartHeight,
        selectedGrowthMarginDriver,
        waterfallMin,
        waterfallMax,
    }: {
        data: any;
        currentGraphWidth: number;
        chartHeight: number;
        selectedGrowthMarginDriver: any;
        waterfallMin: number;
        waterfallMax: number;
    }) => {
        return (
            <div className="hover:bg-black/5" key={`waterfall-chart-${data.attributeDesc as string}`}>
                <WaterFallChartIndividualised
                    containerId={`waterfall-chart-${data.attributeDesc}`}
                    chartWidth={currentGraphWidth}
                    chartHeight={chartHeight}
                    key={`waterfall-chart-${data.attributeDesc}`}
                    dataSet={[data]}
                    xAxisField="attributeDesc"
                    yAxisField={selectedGrowthMarginDriver?.key}
                    colorField="trend"
                    minBound={waterfallMin}
                    maxBound={scaleMaxValue(20, waterfallMax)}
                    toolTipLabel={selectedGrowthMarginDriver?.label}
                />
                <p className="text-center px-0.5">{data.attributeDesc}</p>
            </div>
        );
    },
);

const ProfitGrowthDrivers = () => {
    const chartRef = useRef(null);
    const chartHeight = calculateContainerHeight(2.5);

    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { growthMarginDriversDropDownOptions } = profitPoolConfig;
    const {
        selectedGrowthMarginDriver,
        setSelectedGrowthMarginDriver,
        setSelectedBarForViewBreakdown,
        selectedSubFilter,
    } = useGrowthMarginDriversContext();
    const [toLeft, setToLeft] = useState(0);
    const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});

    const containerWidth = useContainerWidth({
        containerId: 'profit-growth-drivers-infinite-scroll',
        buffer: 300,
    });

    const {
        data: profitGrowthDriversData,
        waterFallChartData,
        currentYearData,
        previousYearData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        min: waterfallMin,
        max: waterfallMax,
    } = useProfitGrowthDriversData();

    const { allDimAndAttributeTypeList } = useContext(FilterContextData);

    const minGraphWidth = 100;
    const currentDataLength = waterFallChartData?.length ?? 0;
    const currentContainerWidth = containerWidth;

    const currentGraphWidth =
        currentContainerWidth / currentDataLength < minGraphWidth
            ? minGraphWidth
            : currentContainerWidth / currentDataLength;

    if (isPending || allDimAndAttributeTypeList?.loading) {
        return (
            <div>
                <p className="text-lg font-semibold">{`${startCase(selectedSubFilter?.dataKey)} ${
                    selectedGrowthMarginDriver?.label
                } Growth`}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                <p className="text-lg font-semibold">{`${startCase(selectedSubFilter?.dataKey)} ${
                    selectedGrowthMarginDriver?.label
                } Growth`}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </div>
        );
    }

    if (isSuccess && isEmpty(profitGrowthDriversData)) {
        return (
            <div>
                <p className="text-lg font-semibold">{`${startCase(selectedSubFilter?.dataKey)} ${
                    selectedGrowthMarginDriver?.label
                } Growth`}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{`${startCase(selectedSubFilter?.dataKey)} ${
                    selectedGrowthMarginDriver?.label
                } Growth`}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <Dropdown
                        menu={{
                            items: growthMarginDriversDropDownOptions,
                            onClick: (info) => {
                                const selectedOption = growthMarginDriversDropDownOptions.find(
                                    (option: any) => option.key === info.key,
                                );
                                setSelectedGrowthMarginDriver(selectedOption);
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <CustomDropdownButton type="default">
                            {selectedGrowthMarginDriver?.label || 'Profit'}
                            <div className="ml-2">
                                <DownSvg />
                            </div>
                        </CustomDropdownButton>
                    </Dropdown>
                    <div className="flex justify-end mb-2 mt-4">
                        {/* <span className="flex gap-2">
                            <CopyToClipBoard refOfComponent={chartRef} />
                        </span> */}
                    </div>
                </div>
            </div>

            <div className="items-center flex flex-row justify-between my-2 mb-4">
                <div className="text-sm">
                    <p>{selectedGrowthMarginDriver?.label}</p>
                </div>
                <div className="flex flex-row gap-2 text-xs">
                    <div className="w-3 h-2 self-center bg-[#5F49BC]" />
                    <p>{'Current and Last Year'}</p>
                    <div className="w-3 h-2 self-center bg-[#42A531]" />
                    <p>{'Positive Growth'}</p>
                    <div className="w-3 h-2 self-center bg-[#CA3D3D]" />
                    <p>{'Negative Growth'}</p>
                </div>
                <div />
            </div>

            {/* Chart */}
            <div className="flex mt-2 flex-row">
                {/* Y Axis */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -2, 0]}
                        barWidth={40}
                        xAxisConfig={false}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`profit-growth-drivers-y-axis`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={50}
                        colorKey={'type'}
                        yAxisConfig={true}
                        YGrid={false}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val);
                        }}
                    />
                </div>
                {/* Last Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Year Ago',
                                totalProfitGrowth: previousYearData ?? 0,
                                type: '#5F49BC',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`profit-growth-drivers-previous-year`}
                        fields={['brand_name', 'totalProfitGrowth', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={100}
                        colorKey={'type'}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return '';
                        }}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: selectedGrowthMarginDriver?.label,
                                value: formatNumWithKMBAlt(val),
                            };
                        }}
                    />
                    <p className="text-center">{'Year Ago'}</p>
                </div>

                {/* WaterFall Chart */}
                <HorizontalInfiniteScroller
                    id={`profit-growth-drivers-infinite-scroll`}
                    hasMore={true}
                    scrollThreshold={0.9}
                    toScrollWidth={toLeft}
                    onNext={(width: number) => {
                        fetchNextPage();
                    }}
                >
                    <div
                        id={`profit-growth-drivers-infinite-scroll`}
                        className={`flex-1 flex overflow-x-auto max-w-full z-10 ${
                            isFetchingNextPage ? 'opacity-50' : ''
                        }`}
                        ref={chartRef}
                    >
                        {waterFallChartData?.map((_datum: any) => {
                            return (
                                <Popover
                                    content={
                                        <>
                                            <p className="text-[10px] text-[#00000073]">{_datum.attributeDesc}</p>
                                            <Button
                                                type="text"
                                                onClick={() => {
                                                    setOpenPopovers({
                                                        ...openPopovers,
                                                        [_datum.attributeDesc]: false,
                                                    });
                                                    setSelectedBarForViewBreakdown(_datum);
                                                }}
                                            >
                                                {'View Breakdown'}
                                            </Button>
                                        </>
                                    }
                                    key={`waterfall-chart-${_datum.attributeDesc}`}
                                    trigger={'click'}
                                    placement="right"
                                    showArrow={true}
                                    open={openPopovers[_datum.attributeDesc as string]}
                                    onOpenChange={(val) =>
                                        setOpenPopovers({ ...openPopovers, [_datum.attributeDesc as string]: val })
                                    }
                                    destroyTooltipOnHide={true}
                                    autoAdjustOverflow={false}
                                >
                                    <>
                                        <MemoisedWaterFallChart
                                            data={_datum}
                                            currentGraphWidth={currentGraphWidth}
                                            chartHeight={chartHeight}
                                            selectedGrowthMarginDriver={selectedGrowthMarginDriver}
                                            waterfallMin={waterfallMin}
                                            waterfallMax={waterfallMax}
                                        />
                                    </>
                                </Popover>
                            );
                        })}
                    </div>
                </HorizontalInfiniteScroller>

                {/* Current Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Current Year',
                                totalProfitGrowth: currentYearData ?? 0,
                                type: '#5F49BF',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`profit-growth-drivers-current-year`}
                        fields={['brand_name', 'totalProfitGrowth', 'type']}
                        minBound={waterfallMin}
                        maxBound={scaleMaxValue(20, waterfallMax)}
                        width={100}
                        colorKey={'type'}
                        YGrid={true}
                        yAxisConfig={true}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: selectedGrowthMarginDriver?.label,
                                value: formatNumWithKMBAlt(val),
                            };
                        }}
                    />
                    <p className="text-center">{'Current Year'}</p>
                </div>
            </div>
        </div>
    );
};

export default memo(ProfitGrowthDrivers);
